import { Subscription, interval } from 'rxjs';
import { ArrayOrSingle } from 'src/modules/sm-base/shared/array-or-single.model';
import { Strings } from "src/modules/utils/shared/strings";
import { Arrays } from './arrays';
import { Dates } from "./dates";
import { EnumType } from './enum-type';
import { Map2 } from './map2.model';
import { ObjectOfSimpleDataTypes } from './object-of-simple-data-types.model';
import { Objects } from "./objects";
import { OrdinaryObject } from './ordinary-object.model';
import { v1 as uuidv1 } from 'uuid';
import handlebars from 'handlebars';
import { colord, extend } from 'colord';
import namesPlugin from 'colord/plugins/names';
import { XMLParser } from 'fast-xml-parser';
import { JwtPayload } from './jwt-payload.model';

extend([namesPlugin]);

export class UtilsInternal {

    alwaysTrue(): boolean {
        return true;
    }

    assignDistinctColors(ids: string[]): OrdinaryObject<number> {
        let palette = this.getMaterialColors();
        let n = palette.length;

        let ordered = Arrays.sortBy([...ids], id => Strings.hash(id));

        const used = new Array<boolean>(n).fill(false);
        const result: OrdinaryObject<number> = {};

        for (const id of ordered) {
            const h = Strings.hash(id);
            let idx = h % n;
            if (!used[idx]) {
                used[idx] = true;
                result[id] = palette[idx];
                continue;
            }
            const step = 1 + h % (n - 1);

            for (let k = 1; k < n; k++) {
                idx = (idx + step) % n;
                if (!used[idx]) {
                    used[idx] = true;
                    result[id] = palette[idx];
                    break;
                }
            }
            if (!Objects.hasProperty(result, id)) {
                result[id] = palette[h % n];
            }
        }

        return result;
    }

    asSingle<T>(value: ArrayOrSingle<T>): T {
        if (Arrays.is(value) && (value as T[]).length > 1) {
            throw new Error("asSingle erwartet Array der Länge 1");
        }
        return Arrays.is(value) ? (value as T[]).length == 0 ? null : (value as T[])[0] : value as T;
    }

    cycleTriState(value: boolean): boolean {
        return value == null ? false : value ? null : true;
    }

    debounce<F extends (...args: any[]) => void>(fn: F, delayMs: number): (...args: Parameters<F>) => void {
        let timer: ReturnType<typeof setTimeout>;
        return (...args: Parameters<F>): void => {
            if (timer != null) {
                clearTimeout(timer);
            }

            timer = this.setTimerOnce(delayMs, () => {
                timer = null;
                fn(...args);
            });
        };
    }

    divSafe(nom: number, denom: number): number {
        return nom / (denom != 0 ? denom : 1);
    }

    errorToString(error: any): string {
        return error.message + "\n" + error.stack;
    }

    expandNumber(n: number, length: number): string {
        let result = Objects.toString(n);
        while (result.length < length) {
            result = "0" + result;
        }
        return result;
    }

    flatten(input: any, path = ''): OrdinaryObject {
        if (Arrays.is(input)) {
          return input.reduce((output: any, value: any, index: any) => ({ ...output, ...this.flatten(value, `${path}[${index}]`) }), {});
        }
        else if (Objects.is(input)) {
          return Object.keys(input as OrdinaryObject).reduce((output, key) => ({ ...output, ...this.flatten(input[key], path ? `${path}.${key}` : key) }), {});
        }
        else {
          return { [path]: input };
        }
    }

    formatNumber(n: number, numDecimalDigits: number, expandDecimalDigits = false, thousandsSep = false, dotIsSep = true): string {
        return n.toLocaleString(dotIsSep ? "en-US" : "de", { maximumFractionDigits: numDecimalDigits, minimumFractionDigits: expandDecimalDigits ? numDecimalDigits : null, useGrouping: thousandsSep });
    }

    generateGuid(): string {
        return uuidv1();
    }

    getArrayOfConstant<T>(constant: T, count: number): T[] {
        let result: T[] = [];
        for (let i = 0; i < count; i++) {
            result.push(constant);
        }
        return result;
    }

    getColorComponents(color: number): number[] {
        return [color >> 24 & 0xff, color >> 16 & 0xff, color >> 8 & 0xff, color & 0xff];
    }

    getDbRelationObject(id: number): any {
        return id == null || id == 0 || id == -1 ? null : { id };
    }

    getEnumNames(cls: EnumType): string[] {
        return Object.keys(cls).filter(item => !Strings.isInt(item));
    }

    getEnumNamesAndValues(cls: EnumType): { index: number; name: string }[] {
        return this.getEnumValues(cls).map(v => ({index: v, name: Objects.toString(cls[v])}));
    }

    getEnumValues(cls: EnumType): number[] {
        return Object.keys(cls).filter(item => Strings.isInt(item)).map(item => Utils.toNumber(item));
    }

    getFileNameWithoutPath(fileName: string): string {
        let index = Math.max(fileName.lastIndexOf("/"), fileName.lastIndexOf("\\"));
        return index == -1 ? fileName : fileName.slice(index + 1);
    }

    getFileNameWithoutPathAndExtension(fileName: string): string {
        return Strings.getSubstringTo(this.getFileNameWithoutPath(fileName), ".");
    }

    getFilePath(fileName: string): string {
        let index = Math.max(fileName.lastIndexOf("/"), fileName.lastIndexOf("\\"));
        return index == -1 ? "" : fileName.slice(0, index);
    }

    getMaterialColors(): number[] {
        return [
            0xf44336, // Red
            0xe91e63, // Pink
            0x9c27b0, // Purple
            0x673ab7, // Deep Purple
            0x3f51b5, // Indigo
            0x2196f3, // Blue
            0x03a9f4, // Light Blue
            0x00bcd4, // Cyan
            0x009688, // Teal
            0x4caf50, // Green
            0x8bc34a, // Light Green
            0xcddc39, // Lime
            0xffeb3b, // Yellow
            0xffc107, // Amber
            0xff9800, // Orange
            0xff5722, // Deep Orange
            0x795548, // Brown
            0x9e9e9e, // Gray
            0x607d8b // Blue Gray
        ];
    }

    getUnmaskedFileName(s: string): string {
        if (s.startsWith("\\\\\\\\")) {
            return Strings.replaceAll(s, "\\\\\\\\", "\\");
        }
        else if (s.startsWith("\\\\")) {
            return s;
        }
        else {
            return Strings.replaceAll(s, "\\\\\\\\", "\\");
        }
    }

    getRandomHash(len = 40): string {
        let result = '';
        const characters = '0123456789ABCDEF';
        for (let i = 0; i < len; i++) {
            result += characters.charAt(Math.floor(Math.random() * characters.length));
        }
        return result;
    }

    getRandomInt(max: number, generator: () => number = null): number {
        return Math.floor(generator != null ? generator() : Math.random() * Math.floor(max));
    }

    getRandomPassword(length: number): string {
        let lowerChars = Arrays.without(Arrays.without(this.getRangeChar("a", "z"), "i"), "l");
        let upperChars = Arrays.without(Arrays.without(this.getRangeChar("A", "Z"), "I"), "O");
        let digitChars = this.getRangeChar("2", "9");
        //let specialChars = ["_", ".", "*", "-", "+", ":", "#", "!", "?", "%", "{", "}", "|", "@", "[", "]", ";", "=", "&", "$", "\\", "/", ",", "(", ")"];
        let chars = [...lowerChars, ...upperChars, ...digitChars/* ...specialChars*/];
        let result: string[] = [...Arrays.getRandom(lowerChars, 1), ...Arrays.getRandom(upperChars, 1), ...Arrays.getRandom(digitChars, 1)/*, ...Arrays.getRandom(specialChars, 1)*/, ...Arrays.getRandom(chars, length - 4)];
        return Arrays.shuffle(result).reduce((s1, s2) => s1 + s2, "");
    }

    getRange(from: number, to: number): number[] {
        let result = [];
        for (let i = from; i <= to; i++) {
            result.push(i);
        }
        return result;
    }

    getRangeChar(from: string, to: string): string[] {
        let result = [];
        for (let i = from.codePointAt(0); i <= to.codePointAt(0); i++) {
            result.push(String.fromCodePoint(i));
        }
        return result;
    }

    getStackTrace(): string {
        try {
            throw new Error("getStackTrace");
        }
        catch (error: any) {
            return error.stack as string;
        }
    }

    getTemplateFunction(template: string): (_: any) => string {
        return handlebars.compile(template);
    }

    getTextColor(bgColor: string): string {
        const c = colord(bgColor);
        return c.isLight() ? "#000000" : "#FFFFFF";
    }

    getWeekDayNames(): string[] {
        return ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];
    }

    getWeekDayName(weekDay: number): string {
        return this.getWeekDayNames()[weekDay];
    }

    getYesNoString(b: boolean): string {
        return b ? "Ja" : "Nein";
    }

    httpQueryItemsToString(params: OrdinaryObject<string>): string {
        let keys = Objects.getPropertyNames(params);
        return keys.length === 0 ? "" : "?" + Arrays.itemsToString(keys, "&", key => key + "=" + Strings.escapeHttpQueryParam(params[key]));
    }

    isBool(object: any): boolean {
        return typeof object === "boolean";
    }

    isEanValid(ean: string): boolean {
        if (ean.length != 13 && ean.length != 14) {
            return false;
        }

        let sum = 0;
        const length = ean.length;

        for (let i = 0; i < length - 1; i++) {
            const digit = Number.parseInt(ean[i], 10);
            sum += (i % 2 === 0 ? 1 : 3) * digit;
        }

        const calculatedCheckDigit = (10 - sum % 10) % 10;
        const actualCheckDigit = Number.parseInt(ean[length - 1], 10);

        return calculatedCheckDigit === actualCheckDigit;
    }

    isEmailAddressValid(email: string): boolean {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
        return emailRegex.test(email);
    }

    isFileName(s: string): boolean {
        return Strings.regexMatches(s, "([A-Z]:(\\\\\\\\|\\\\|/)|\\\\{4}|\\\\{2}|/).*");
    }

    isFunction(o: any): boolean {
        return typeof o === "function";
    }

    isInt(object: any): boolean {
        return this.isNumber(object) && Number.isInteger(object as number);
    }

    isIntString(s: string, trim = true): boolean {
        try {
            return Number.isInteger(this.toNumber(trim ? s.trim() : s));
        }
        catch {
            return false;
        }
    }

    isIntOrIntString(object: any, trim = true): boolean {
        return this.isInt(object) || Strings.is(object) && this.isIntString(trim ? (object as string).trim() : object as string);
    }

    isNode(): boolean {
        return typeof process !== 'undefined' && process.release.name === 'node';
    }

    isNoe(object: any): boolean {
        return object == null || Arrays.is(object) && object.length === 0 || object === "";
    }

    isNumber(object: any): boolean {
        return typeof object === "number";
    }

    isNumberString(s: string): boolean {
        try {
            this.toNumber(s);
            return true;
        }
        catch {
            return false;
        }
    }

    isNumberOrNumberString(object: any): boolean {
        return this.isNumber(object) || Strings.is(object) && this.isNumberString(object as string);
    }

    isJwtValid(token: string, validateLifetime = true): boolean {
        let payload = JwtPayload.ofToken(token);
        if (payload == null) {
            return false;
        }

        if (!validateLifetime) {
            return true;
        }

        let nowSeconds = Math.floor(Date.now() / 1000);

        if (payload.nbf != null && nowSeconds < payload.nbf) {
            return false;
        }

        if (payload.exp != null && nowSeconds >= payload.exp) {
            return false;
        }

        return true;
    }

    makeFunctionFromCode(code: string): (...args: any[]) => any {
        // eslint-disable-next-line no-new-func
        return new Function(code) as (...args: any[]) => any;
    }

    nop(): void {
    }

    parseRequestParams(params: any, neededParams: ObjectOfSimpleDataTypes): OrdinaryObject {
        let result: OrdinaryObject = {};
        if (neededParams != null) {
            for (let neededParam of Objects.getPropertyNames(neededParams)) {
                let paramValue: any = params[neededParam];
                let isOptional = neededParams[neededParam].endsWith("?");
                let type = Strings.removeSuffix(neededParams[neededParam], "?");
                let isArray = type.endsWith("[]");
                type = Strings.removeSuffix(type, "[]");
                if (paramValue == null) {
                    if (!isOptional) {
                        throw new Error("Parameter '" + neededParam + "' fehlt");
                    }
                }
                else {
                    try {
                        if (isArray) {
                            switch (type) {
                                case "string":
                                    paramValue = Strings.split(Objects.toString(paramValue), ";").map(item => Objects.toString(item));
                                    break;
                                case "number":
                                    paramValue = Strings.split(Objects.toString(paramValue), ";").map(item => Utils.toNumber(item));
                                    break;
                                case "bool":
                                    paramValue = Strings.split(Objects.toString(paramValue), ";").map(item => Utils.toBool(item));
                                    break;
                                case "date":
                                    paramValue = Strings.split(Objects.toString(paramValue), ";").map(item => Dates.fromString(item, "YYYYMMDD"));
                            }
                        }
                        else {
                            switch (type) {
                                case "string":
                                    paramValue = Objects.toString(paramValue);
                                    break;
                                case "number":
                                    paramValue = Utils.toNumber(paramValue);
                                    break;
                                case "bool":
                                    paramValue = Utils.toBool(paramValue);
                                    break;
                                case "date":
                                    paramValue = Dates.fromString(paramValue as string, "YYYYMMDD");
                            }
                        }
                    }
                    catch (error: any) {
                        throw new Error("Der Anfrageparameter " + neededParam + " muss vom Typ " + type + " sein. Der übergebene Wert " + paramValue + " konnte nicht konvertiert werden\n" + this.errorToString(error));
                    }
                }
                result[neededParam] = paramValue;
            }
        }
        for (let key of Objects.getPropertyNames(params)) {
            if (!Objects.hasProperty(result, key)) {
                result[key] = params[key];
            }
        }
        return result;
    }

    restrictDecimalDigits(value: number, numDigits: number, round = true): string {
        if (round) {
            return value.toFixed(numDigits);
        }
        else {
            const factor = 10 ** numDigits;
            const truncatedValue = Math.trunc(value * factor) / factor;
            return truncatedValue.toFixed(numDigits);
        }
    }

    round(x: number, decimals = 0): number {
        let factor = 10 ** decimals;
        return (x >= 0 ? Math.floor(x * factor + 0.5) : Math.ceil(x * factor - 0.5)) / factor;
    }

    roundToDecimal(value: number, step: number): number {
        return Math.round(value / step) * step;
    }

    serializeForm(obj: any, prefix: string): string {
        let result = [];
        for (let p of Objects.getPropertyNames(obj)) {
            let k = prefix ? prefix + "[" + p + "]" : p;
            let v = obj[p];
            result.push(v != null && typeof v === "object" ?
                this.serializeForm(v, k) :
                Strings.escapeHttpQueryParam(k) + "=" + Strings.escapeHttpQueryParam(v as string));
        }
        return result.join("&");
    }

    setTimer(millis: number, onTimer: () => void): Subscription {
        return interval(millis).subscribe(_ => onTimer());
    }

    setTimerOnce(millis: number, onTimer: () => void): any {
        // eslint-disable-next-line no-restricted-syntax
        return setTimeout(onTimer, millis);
    }

    async sleep(ms: number): Promise<any> {
        return new Promise(resolve => {
            this.setTimerOnce(ms, () => resolve);
        });
    }

    stretchNumbers(data: number[], sum: number): number[] {
        let factor = sum / Arrays.sum(data);
        return data.map(n => n * factor);
    }

    toBinaryString(n: number): string {
        return (n >>> 0).toString(2);
    }

    toBool(object: any, def: boolean = null): boolean {
        if (typeof object === "boolean") {
            return object;
        }
        else if (typeof object === "number") {
            if (object === 0) {
                return false;
            }
            else if (object === 1) {
                return true;
            }
            else {
                if (def != null) {
                    return def;
                }
                throw new Error("Konnte '" + object + "' nicht in boolean konvertieren");
            }
        }
        else if (Strings.is(object)) {
            if (object === "0" || Strings.equalsCi(object as string, "false")) {
                return false;
            }
            else if (object === "1" || Strings.equalsCi(object as string, "true")) {
                return true;
            }
            else {
                if (def != null) {
                    return def;
                }
                throw new Error("Konnte '" + object + "' nicht in boolean konvertieren");
            }
        }
        else {
            if (def != null) {
                return def;
            }
            throw new Error("Konnte '" + object + "' nicht in boolean konvertieren");
        }
    }

    toByteSize(value: number, exact = true): [number, string] {
        if (value == 0) {
            return [0, "Bytes"];
        }
        let names1 = ["Bytes", "KB", "MB", "GB", "TB"];
        let names2 = ["Bytes", "KiB", "MiB", "GiB", "TiB"];
        for (let i = names1.length - 1; i >= 1; i--) {
            if (value % 1_024 ** i == 0) {
                return [value / 1_024 ** i, names1[i]];
            }
            if (value % 1_000 ** i == 0) {
                return [value / 1_000 ** i, names2[i]];
            }
        }
        if (!exact) {
            for (let i = names1.length - 1; i >= 1; i--) {
                if (value / 1_024 ** i >= 10) {
                    return [value / 1_024 ** i, names1[i]];
                }
            }
        }
        return [value, names1[0]];
    }

    toByteSizeString(value: number, exact = true): string {
        if (value == 1 || value == 0) {
            return value + " Byte";
        }
        let result = this.toByteSize(value, exact);
        return this.restrictDecimalDigits(result[0], 0)/*.replace(".00", "")*/ + " " + result[1];
    }

    toDuration(value: number): [number, string] {
        if (value == 0) {
            return [0, "Sekunden"];
        }
        if (value % (60 * 60 * 24) == 0) {
            return [value / (60 * 60 * 24), "Tage"];
        }
        if (value % (60 * 60) == 0) {
            return [value / (60 * 60), "Stunden"];
        }
        if (value % 60 == 0) {
            return [value / 60, "Minuten"];
        }
        return [value, "Sekunden"];
    }

    toDurationString(value: number): string {
        if (value == 1) {
            return value + " Sekunde";
        }
        let result = this.toDuration(value);
        return result[0] + " " + (result[0] == 1 ? Strings.removeSuffix(Strings.removeSuffix(result[1], "e"), "n") : result[1]);
    }

    toDurationStringAdvanced(millis: number, numSignificant: number, short = false): string {
        const millisPerSecond = 1_000;
        const millisPerMinute = 60 * millisPerSecond;
        const millisPerHour = 60 * millisPerMinute;
        const millisPerDay = 24 * millisPerHour;

        const days = Math.floor(millis / millisPerDay);
        const hours = Math.floor(millis % millisPerDay / millisPerHour);
        const minutes = Math.floor(millis % millisPerHour / millisPerMinute);
        const seconds = Math.floor(millis % millisPerMinute / millisPerSecond);
        const millis2 = Math.floor(millis % millisPerSecond);

        const units = [
          { value: days, label: short ? "d" : "Tag", pluralLabel: short ? "d" : "Tage" },
          { value: hours, label: short ? "h" : "Stunde", pluralLabel: short ? "h" : "Stunden" },
          { value: minutes, label: short ? "m" : "Minute", pluralLabel: short ? "m" : "Minuten" },
          { value: seconds, label: short ? "s" : 'Sekunde', pluralLabel: short ? "s" : "Sekunden" },
          { value: millis2, label: short ? "ms" : 'Millisekunde', pluralLabel: short ? "ms" : "Millisekunden" }
        ];

        let result = '';
        let count = 0;
        for (const unit of units) {
          if (unit.value > 0) {
            const label = unit.value === 1 ? unit.label : unit.pluralLabel;
            result += unit.value + " " + label + ", ";
            count++;
            if (count === numSignificant || count == numSignificant - 1 && unit.value >= 10) {
              break;
            }
          }
        }

        return result.trim().slice(0, -1);
    }

    toDurationStringAdvancedNanos(nanos: number, numSignificant: number, short = false): string {
        const nanosPerMicro = 1_000;
        const nanosPerMilli = 1_000 * nanosPerMicro;
        const nanosPerSecond = 1_000 * nanosPerMilli;
        const nanosPerMinute = 60 * nanosPerSecond;
        const nanosPerHour = 60 * nanosPerMinute;
        const nanosPerDay = 24 * nanosPerHour;

        const days = Math.floor(nanos / nanosPerDay);
        const hours = Math.floor(nanos % nanosPerDay / nanosPerHour);
        const minutes = Math.floor(nanos % nanosPerHour / nanosPerMinute);
        const seconds = Math.floor(nanos % nanosPerMinute / nanosPerSecond);
        const millis = Math.floor(nanos % nanosPerSecond / nanosPerMilli);
        const micros = Math.floor(nanos % nanosPerMilli / nanosPerMicro);
        const nanos2 = Math.floor(nanos % nanosPerMicro);

        const units = [
          { value: days, label: short ? "d" : "Tag", pluralLabel: short ? "d" : "Tage" },
          { value: hours, label: short ? "h" : "Stunde", pluralLabel: short ? "h" : "Stunden" },
          { value: minutes, label: short ? "m" : "Minute", pluralLabel: short ? "m" : "Minuten" },
          { value: seconds, label: short ? "s" : 'Sekunde', pluralLabel: short ? "s" : "Sekunden" },
          { value: millis, label: short ? "ms" : 'Millisekunde', pluralLabel: short ? "ms" : "Millisekunden" },
          { value: micros, label: short ? "µs" : 'Mikrosekunde', pluralLabel: short ? "µs" : "Mikrosekunden" },
          { value: nanos2, label: short ? "ns" : 'Nanosekunde', pluralLabel: short ? "ns" : "Nanosekunden" }
        ];

        let result = '';
        let count = 0;
        for (const unit of units) {
          if (unit.value > 0) {
            const label = unit.value === 1 ? unit.label : unit.pluralLabel;
            result += unit.value + " " + label + ", ";
            count++;
            if (count === numSignificant || count == numSignificant - 1 && unit.value >= 10) {
              break;
            }
          }
        }

        return result.trim().slice(0, -1);
    }

    toColorHexString(color: number | string): string {
        return this.isNumber(color) ? "#" + (color as number).toString(16).padStart(6, "0") : (color as string);
    }

    toColorNumber(color: string | number): number {
        return this.isNumber(color) ? color as number : Number.parseInt((color as string).replace("#", "0x"), 16);
    }

    toNumber(object: any, nullIs0 = false, emptyIs0 = false, ifInvalid: number = null): number {
        if (typeof object === "boolean") {
            return object ? 1 : 0;
        }
        else if (typeof object === "number") {
            return object;
        }
        else if (Strings.is(object)) {
            if (emptyIs0 && object == "") {
                return 0;
            }
            let result = Strings.regexMatches(object as string, "-?[0-9]+([\\.,][0-9]+)?") ? Number.parseFloat((object as string).replace(",", ".")) : null;
            if (result == null || Number.isNaN(result)) {
                if (ifInvalid == null) {
                    throw new TypeError("Konnte '" + object + "' nicht in number konvertieren");
                }
                else {
                    return ifInvalid;
                }
            }
            return result;
        }
        else if (object == null && nullIs0) {
            return 0;
        }
        else if (ifInvalid == null) {
                throw new TypeError("Konnte '" + object + "' nicht in number konvertieren");
            }
            else {
                return ifInvalid;
            }
    }

    topologicalSort<T>(items: T[], dependsOn: (o1: T, o2: T) => boolean, onCircularReference?: (o: T) => void): T[] {
        const adjList = new Map2<T, T[]>();
        for (let item of items) {
            adjList.set(item, items.filter(otherItem => dependsOn(item, otherItem)));
        }

        let visited = new Set<T>();
        let temp = new Set<T>();
        let sorted: T[] = [];

        const visit = (item: T): void => {
            if (temp.has(item)) {
                if (onCircularReference != null) {
                    onCircularReference(item);
                }
            }
            else if (!visited.has(item)) {
                temp.add(item);
                adjList.get(item).forEach(visit);
                temp.delete(item);
                visited.add(item);
                sorted.push(item);
            }
        };

        for (let item of items) {
            if (!visited.has(item)) {
                visit(item);
            }
        }

        return sorted;
    }

    truncateDecimals(value: number, decimals: number): number{
        let factor = 10**decimals;
        return Math.trunc(value * factor) / factor;
    }
    
    xmlToObject(xml: string): any {
        return new XMLParser({
            ignoreAttributes: false,
            alwaysCreateTextNode: true,
            textNodeName: "_text",
            attributeNamePrefix: "@_",
            parseTagValue: false, //TagValues bleiben immer strings
            parseAttributeValue: false //AttributeValues bleiben immer strings
        }).parse(xml);
    }

    xmlToJson(xml: string): string {
        return Objects.toJson(this.xmlToObject(xml), true);
    }

}

export let Utils = new UtilsInternal();